import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';

import AdapterError, { InvalidError } from '@ember-data/adapter/error';
import RESTAdapter from '@ember-data/adapter/rest';
import type { Snapshot, SnapshotRecordArray } from '@ember-data/legacy-compat/-private';

import type { Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import type BoardShareRecord from 'webapp/models/board-share';
import type GoalLogRecord from 'webapp/models/goal-log';
import type TeamSectionRecord from 'webapp/models/team-section';
import type TeamSectionItemRecord from 'webapp/models/team-section-item';

import { Severity } from 'webapp/services/logger';
import type { EDRequestError, FieldError } from 'webapp/services/request';

// one signature for all the overloads of RESTAdapter::buildURL
type BuildURL = (
	modelName: string,
	id: string | string[] | null,
	snapshot?: Snapshot | Snapshot[] | SnapshotRecordArray | null,
	requestType?: string,
	query?: Record<string, unknown>,
) => string;

export default class ApplicationAdapter extends RESTAdapter {
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;

	override namespace = 'd';

	// @ts-expect-error - is defined as a property in RESTAdapter, but overridden here as an accessor
	get host() {
		return this.requestService.apiUrl!;
	}

	// @ts-expect-error - is defined as a property in RESTAdapter, but overridden here as an accessor
	get headers() {
		const currentUserService = this.currentUserService;
		return {
			Authorization: currentUserService.authTokenAuthorizationHeader,
			'X-Authorization': currentUserService.userTokenAuthorizationHeader,
			'X-Request-Id': this.requestService.generateRequestId(),
		};
	}

	override shouldReloadAll() {
		return true;
	}

	override buildURL(
		type: string,
		id: string | string[] | null,
		snapshot?: Snapshot | Snapshot[] | SnapshotRecordArray | null,
		requestType?: string,
		query?: Record<string, unknown>,
	) {
		let url = (super.buildURL as BuildURL).call(this, type, id, snapshot, requestType, query);

		if (type === ModelName.CUSTOM_QUERY) {
			url = url.toLowerCase();
		}
		if (type === ModelName.BOARD_SHARE) {
			if (['createRecord', 'updateRecord', 'deleteRecord'].includes(requestType!)) {
				const boardId =
					((snapshot as Snapshot | undefined)?.record as BoardShareRecord | undefined)
						?.boardId || null;
				url = url.replace('boardShares', `boards/${boardId}/shares`);
			} else {
				url = url.replace('boardShares', 'boards/shares');
			}
		}
		if (type === ModelName.GOAL_LOG) {
			if (['createRecord', 'updateRecord', 'deleteRecord'].includes(requestType!)) {
				const goalId =
					((snapshot as Snapshot | undefined)?.record as GoalLogRecord | undefined)
						?.goalId || null;
				url = url.replace('goalLogs', `goals/${goalId}/logs`);
			} else {
				url = url.replace('goalLogs', 'goals/logs');
			}
		}
		if (type === ModelName.REPORT_SCHEDULE_SETTING) {
			// type report-schedule-setting has report-schedule endpoint
			// type report-schedule is not requested through ED endpoints
			url = url.replace('reportScheduleSetting', 'report-schedule');
		}

		if (type === ModelName.DATA_SELECT) {
			url = url.replace('dataSelect', 'data-select');
		}

		if (type === ModelName.METRIC_ANOMALY) {
			url = url.replace('metricAnomaly', 'metric-anomaly');
		}

		if (type === ModelName.TEAM_SECTION) {
			if (['deleteRecord', 'updateRecord'].includes(requestType!)) {
				const teamId =
					((snapshot as Snapshot | undefined)?.record as TeamSectionRecord | undefined)
						?.teamId || null;

				url = url.replace('teamSections', `teams/${teamId}/sections`);
			}
		}

		if (type === ModelName.TEAM_SECTION_ITEM) {
			if (['updateRecord', 'deleteRecord'].includes(requestType!)) {
				const teamSectionId =
					(
						(snapshot as Snapshot | undefined)?.record as
							TeamSectionItemRecord | undefined
					)?.sectionId || null;
				const teamId = teamSectionId
					? this.mmTeamSectionsService.findModel(String(teamSectionId))?.teamId || null
					: null;

				url = url.replace(
					'teamSectionItems',
					`teams/${teamId}/sections/${teamSectionId}/section-item`,
				);
			}
		}

		url += this.requestService.debugParam || '';

		if (window.isSnapshooterActive) {
			this.loggerService.logError(
				'Requests on active snapshot',
				{ url, requestType, token: this.requestService.wallToken },
				Severity.CRITICAL,
			);
		}

		return url;
	}

	override pathForType(type: string) {
		let defaultPath = super.pathForType(type);
		const dasherized = [
			ModelName.ALERT_SETTING,
			ModelName.BOARD_LOOP,
			ModelName.REPORT_SLIDE,
			ModelName.REPORT_SLIDE_FRAGMENT,
			ModelName.METRIC_ANOMALY,
			ModelName.CLIENT_METRIC,
			ModelName.HORIZON_METRIC,
		];
		if (dasherized.includes(type as ModelName)) {
			defaultPath = dasherize(defaultPath);
		}
		return defaultPath;
	}

	handlePayloadCustom(payload: Record<string, unknown>) {
		return payload; // extended
	}

	// @ts-expect-error - Return types mismatch
	handleResponse(
		status: number,
		headers: Record<string, string>,
		payload: Record<string, unknown>,
		requestData: { method: string; url: string },
	) {
		if (this.requestService.isForceRelogging) {
			// don't throw more errors, don't even resolve
			return new Promise((resolve) => {
				later(resolve, true, 5000);
			});
		}

		let errors: FieldError[] = [];

		let errorObj: EDRequestError;

		if (this.isSuccess(status, headers, payload)) {
			return this.handlePayloadCustom(payload);
		} else {
			errors = this.requestService.parseErrors(payload);
			const normalizedErrorResponse = this.normalizeErrorResponse(status, headers, payload);
			errorObj = new AdapterError(normalizedErrorResponse);
			if (this.isReload(status)) {
				this.requestService.handleTeapot();
			} else if (this.isExpiredToken(status, errors)) {
				this.requestService.handleExpiredToken();
			} else if (this.isInvalid(status, headers, payload)) {
				errorObj = new InvalidError(
					errors.map((err) => {
						return this.formatErrorData(err.field, err.message);
					}),
				);
			} else {
				const errorsString = errors
					.map((err) => {
						return err.message;
					})
					.join(' | ');
				// no messages: AdapterError's own default errors
				errorObj = new AdapterError(
					errorsString ? [{ title: 'Adapter Error', detail: errorsString }] : undefined,
				);
			}
		}

		errorObj.originalErrors = errors;
		errorObj.originalRequest = {
			// sadly, requestData does not return anything else
			// so we have no access to headers, namely requestId
			url: requestData.method + ' ' + requestData.url,
		};
		errorObj.originalResponse = {
			status: status,
			payload: payload,
		};

		return errorObj;
	}

	private isReload(status: number) {
		return this.requestService.isReload(status);
	}

	private isExpiredToken(status: number, errors: FieldError[]) {
		return this.requestService.isExpiredToken(status, errors);
	}

	private formatErrorData(field: string, message: string) {
		return {
			detail: message,
			source: {
				pointer: 'data/attributes/' + field,
			},
		};
	}
}
